import { usePostDefensive, useUpdateDefensive } from '@practium/features/api';
import { useToast } from '@practium/ui';
import { useRef, useState } from 'react';
import { useUpdateAvatarsDefensive } from '../../api/hooks/defensives/useUpdateAvatarsFertilizer';
import { DEFAULT_DEFENSIVE_FORM } from '../constants/defensive.constants';
import { DefensiveSchema } from '../schemas/defensive.schema';
import type { DefensiveFormData, Manufacturer } from '../types';

export interface UseDefensiveFormOptions {
	manufacturers: Manufacturer[];
	initialData?: DefensiveFormData;
	onSubmit: () => void;
	id?: string;
	file?: File[];
}

const FIELD_LABELS: Record<string, string> = {
	commercialName: 'Nome Comercial',
	manufacturerId: 'Fabricante',
	nature: 'Natureza do Produto',
	class: 'Classe Agronômica',
	culture: 'Cultura Recomendada',
	applicationForm: 'Forma de Aplicação',
	dose: 'Dose Base',
	doseMeasurementUnit: 'Unidade de Dose',
	activeIngredients: 'Composição',
};

const normalizeApplicationForm = (value: DefensiveFormData['applicationForm'] | string | undefined): string[] => {
	if (Array.isArray(value)) return value;
	return value ? [value] : [];
};

const normalizeCulture = (value: string[] | string | undefined): string[] => {
	if (Array.isArray(value)) return value.filter(Boolean);
	return value ? [value] : [];
};

const isExclusiveFertigation = (applicationForm: string[]): boolean =>
	applicationForm.length === 1 && applicationForm[0] === 'irrigation';

export const useDefensiveForm = ({ manufacturers, initialData, onSubmit, id, file = [] }: UseDefensiveFormOptions) => {
	const toast = useToast();
	const nextId = useRef(0);
	const newId = () => ++nextId.current;
	const initialApplicationForm = normalizeApplicationForm(initialData?.applicationForm);
	const initialForm = initialData
		? {
			...DEFAULT_DEFENSIVE_FORM,
			...initialData,
			activeIngredients: [
				{ name: '', concentration: '' },
				...(initialData.activeIngredients ?? []).filter(row => row.name || row.concentration),
			],
			applicationForm: initialApplicationForm,
			culture: normalizeCulture(initialData.culture),
			syrupVolume: isExclusiveFertigation(initialApplicationForm) ? '' : initialData.syrupVolume,
		}
		: { ...DEFAULT_DEFENSIVE_FORM, activeIngredients: [{ name: '', concentration: '' }] };
	const [form, setForm] = useState<DefensiveFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [ingredientKeys, setIngredientKeys] = useState<number[]>(() =>
		Array.from({ length: initialForm.activeIngredients.length }, newId)
	);
	const [classificationRows, setClassificationRows] = useState(() => {
		const rows = initialForm.class.map(item => ({ id: newId(), class: item.class, group: item.group ?? '' }));
		return [{ id: newId(), class: '', group: '' }, ...rows];
	});
	const initialClassificationRows = useRef(classificationRows);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [compositionErrors, setCompositionErrors] = useState<
		{ nameError: boolean; concentrationError: boolean }[]
	>([]);

	const { mutateAsync: createDefensive } = usePostDefensive();
	const { mutateAsync: updateDefensive } = useUpdateDefensive();
	const { mutateAsync: updateAvatarsDefensive } = useUpdateAvatarsDefensive();

	const set = (key: keyof DefensiveFormData, value: unknown) =>
		setForm(f => ({ ...f, [key]: value }));

	const changeClassificationRows = (rows: typeof classificationRows) => {
		setClassificationRows(rows);
		const classes = rows.filter(row => row.class).map(row => ({
			class: row.class,
			group: row.group.trim().toUpperCase() || undefined,
		}));
		setForm(f => ({ ...f, class: classes }));
		setErrors(previous => ({ ...previous, class: '' }));
		return classes;
	};

	const updateClassificationRow = (id: number, field: 'class' | 'group', value: string) =>
		changeClassificationRows(classificationRows.map(row =>
			row.id === id ? { ...row, [field]: value } : row));

	const addClassificationRow = () => {
		const [entry, ...addedRows] = classificationRows;
		if (!entry.class) return;
		changeClassificationRows([
			{ ...entry, class: '', group: '' },
			...addedRows,
			{ ...entry, id: newId(), group: entry.group.trim().toUpperCase() },
		]);
	};

	const removeClassificationRow = (id: number) => {
		const rows = classificationRows.filter(row => row.id !== id);
		return changeClassificationRows(rows.length ? rows : [{ id: newId(), class: '', group: '' }]);
	};

	const setApplicationForm = (applicationForm: string[]) =>
		setForm((currentForm) => ({
			...currentForm,
			applicationForm,
			syrupVolume: isExclusiveFertigation(applicationForm) ? '' : currentForm.syrupVolume,
		}));

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current) || JSON.stringify(classificationRows) !== JSON.stringify(initialClassificationRows.current);

	const updateIngredient = (index: number, field: 'name' | 'concentration', value: string) => {
		setForm((f) => {
			const next = [...f.activeIngredients];
			next[index] = { ...next[index], [field]: value };
			return { ...f, activeIngredients: next };
		});
	};

	const addIngredient = () => {
		const entry = form.activeIngredients[0];
		if (!entry?.name || !entry.concentration) return;
		const id = newId();
		setForm((f) => ({
			...f,
			activeIngredients: [
				{ name: '', concentration: '' },
				...f.activeIngredients.slice(1),
				f.activeIngredients[0],
			],
		}));
		setIngredientKeys((keys) => [...keys, id]);
		setCompositionErrors(previous => [
			{ nameError: false, concentrationError: false },
			...form.activeIngredients.slice(1).map((_, index) =>
				previous[index + 1] ?? { nameError: false, concentrationError: false }),
			{ nameError: false, concentrationError: false },
		]);
	};

	const removeIngredient = (index: number) => {
		if (index === 0) return;
		setForm((f) => ({
			...f,
			activeIngredients: f.activeIngredients.filter((_, i) => i !== index),
		}));
		setIngredientKeys((k) => k.filter((_, i) => i !== index));
		setCompositionErrors(previous => previous.filter((_, i) => i !== index));
	};

	const updateIngredientWithClear = (
		index: number,
		field: 'name' | 'concentration',
		value: string
	) => {
		updateIngredient(index, field, value);
		setCompositionErrors((prev) => {
			const next = [...prev];
			if (next[index]) next[index] = { ...next[index], [`${field}Error`]: false };
			return next;
		});
	};

	const handleSubmit = async () => {
		if (classificationRows.some(row => row.group.trim() && !row.class)) {
			setErrors(previous => ({ ...previous, class: 'Selecione a classe de cada grupo informado' }));
			toast.error('Selecione a classe de cada grupo informado');
			return;
		}
		const rowErrors = form.activeIngredients.map((row) => ({
			nameError: !row.name && !!row.concentration,
			concentrationError: !!row.name && !row.concentration,
		}));
		const hasRowErrors = rowErrors.some((r) => r.nameError || r.concentrationError);
		setCompositionErrors(rowErrors);

		const result = DefensiveSchema.safeParse(form);

		if (hasRowErrors) {
			toast.error('Preencha o ingrediente e a concentração de cada linha de composição');
			return;
		}

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [
				...new Set(result.error.issues.map((i) => String(i.path[0] ?? '')).filter(Boolean)),
			];
			const labels = uniquePaths.map((p) => FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		setCompositionErrors([]);
		const manufacturer = manufacturers.find((m) => m._id === form.manufacturerId);
		if (!manufacturer) {
			toast.error('Fabricante não encontrado');
			return;
		}

		setIsLoading(true);
		try {
			const payload = {
				dose: Number(form.dose),
				commercialName: form.commercialName || undefined,
				class: form.class.map(item => ({
					class: item.class,
					...(item.group?.trim() ? { group: item.group.trim().toUpperCase() } : {}),
				})),
				culture: form.culture,
				doseMeasurementUnit: form.doseMeasurementUnit || undefined,
				commonPests: form.commonPests ? [form.commonPests] : [],
				syrupVolume:
					!isExclusiveFertigation(form.applicationForm) && form.syrupVolume
						? Number(form.syrupVolume)
						: undefined,
				activeIngredients: form.activeIngredients
					.filter((i) => i.name)
					.map((i) => ({ name: i.name, concentration: Number(i.concentration) })),
				nature: form.nature || undefined,
				applicationForm: form.applicationForm.length > 0 ? form.applicationForm : undefined,
				manufacturer: manufacturer._id,
			};
			if (id) {
				await updateDefensive({ id, data: payload });
				try {
					if (file.length) {
						await updateAvatarsDefensive({ id, file });
					}
				} catch {
					toast.error('Erro ao atualizar avatar do defensivo');
				}
				toast.success('Defensivo atualizado com sucesso!');
			} else {
				await createDefensive({
					...payload,
					...(file.length ? { file } : {}),
				});
				toast.success('Defensivo salvo com sucesso!');
			}
			onSubmit();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar defensivo');
		} finally {
			setIsLoading(false);
		}
	};

	return {
		form,
		ingredientKeys,
		errors,
		compositionErrors,
		isLoading,
		set,
		setApplicationForm,
		classificationRows,
		updateClassificationRow,
		addClassificationRow,
		removeClassificationRow,
		updateIngredient: updateIngredientWithClear,
		addIngredient,
		removeIngredient,
		handleSubmit,
		isDirty,
	};
};
